import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Phone,
  Search, 
  Download, 
  Trash2, 
  CheckCircle, 
  Clock, 
  MessageSquare, 
  Copy, 
  Check, 
  Sparkles,
  Heart,
  Eye,
  X,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';

const InquiriesManagement = () => {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    replied: 0,
    generalInquiries: 0,
    sponsorshipInquiries: 0
  });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  
  // Filters
  const [selectedTopic, setSelectedTopic] = useState('all'); // 'all', 'General Inquiry', 'Sponsorship & Partnerships'
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all', 'unread', 'read', 'replied'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail Modal
  const [activeModalInquiry, setActiveModalInquiry] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Fetch logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.get('/about-page');
        if (response.ok) {
          const data = await response.json();
          if (data.logo) setLogoUrl(data.logo);
        }
      } catch (err) {
        console.error('Error fetching logo:', err);
      }
    };
    fetchLogo();
  }, []);

  // Fetch inquiries from backend
  const fetchInquiries = async (showToast = false) => {
    setIsRefreshing(true);
    try {
      const response = await api.get('/contact');
      if (response.ok) {
        const data = await response.json();
        const list = data.inquiries || [];
        setInquiries(list);
        if (data.stats) {
          setStats(data.stats);
        } else {
          computeStats(list);
        }
        if (showToast) toast.success('Inquiries refreshed successfully!');
      } else {
        toast.error('Failed to load inquiries.');
      }
    } catch (error) {
      console.error('Error loading inquiries:', error);
      toast.error('Failed to connect to server.');
    } finally {
      setLoading(false);
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const computeStats = (list) => {
    const s = {
      total: list.length,
      unread: 0,
      read: 0,
      replied: 0,
      generalInquiries: 0,
      sponsorshipInquiries: 0
    };
    list.forEach(i => {
      if (i.status === 'unread') s.unread++;
      if (i.status === 'read') s.read++;
      if (i.status === 'replied') s.replied++;

      const sub = (i.subject || '').toLowerCase();
      if (sub.includes('sponsor') || sub.includes('partner')) {
        s.sponsorshipInquiries++;
      } else {
        s.generalInquiries++;
      }
    });
    setStats(s);
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  // Update status (e.g. read, replied, unread)
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const res = await api.patch(`/contact/${id}/status`, { status: newStatus });
      if (res.ok) {
        setInquiries(prev => prev.map(inq => inq.id === id ? { ...inq, status: newStatus } : inq));
        // Update local stats
        setStats(prev => {
          const next = { ...prev };
          next.unread = inquiries.filter(i => (i.id === id ? newStatus : i.status) === 'unread').length;
          next.read = inquiries.filter(i => (i.id === id ? newStatus : i.status) === 'read').length;
          next.replied = inquiries.filter(i => (i.id === id ? newStatus : i.status) === 'replied').length;
          return next;
        });
        toast.success(`Marked as ${newStatus}`);
        if (activeModalInquiry?.id === id) {
          setActiveModalInquiry(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        toast.error('Failed to update status.');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Network error updating status.');
    }
  };

  // Delete inquiry
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;

    try {
      const res = await api.delete(`/contact/${id}`);
      if (res.ok) {
        setInquiries(prev => prev.filter(i => i.id !== id));
        toast.success('Inquiry deleted');
        if (activeModalInquiry?.id === id) {
          setActiveModalInquiry(null);
        }
        fetchInquiries();
      } else {
        toast.error('Failed to delete inquiry.');
      }
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      toast.error('Error deleting inquiry.');
    }
  };

  // Copy email
  const handleCopyEmail = (email, id) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (inquiries.length === 0) {
      toast.error('No inquiries to export');
      return;
    }

    const headers = ['ID', 'Date', 'Name', 'Email', 'Phone', 'Topic/Subject', 'Status', 'Message'];
    const rows = inquiries.map(inq => [
      `"${inq.id || ''}"`,
      `"${new Date(inq.createdAt).toLocaleString()}"`,
      `"${(inq.name || '').replace(/"/g, '""')}"`,
      `"${(inq.email || '').replace(/"/g, '""')}"`,
      `"${(inq.phone || '').replace(/"/g, '""')}"`,
      `"${(inq.subject || '').replace(/"/g, '""')}"`,
      `"${inq.status || 'unread'}"`,
      `"${(inq.message || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `bow_contact_inquiries_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Inquiries exported as CSV!');
  };

  // Filtered list
  const filteredInquiries = inquiries.filter(inq => {
    // Topic filter
    if (selectedTopic !== 'all') {
      const isSponsor = (inq.subject || '').toLowerCase().includes('sponsor');
      if (selectedTopic === 'Sponsorship & Partnerships' && !isSponsor) return false;
      if (selectedTopic === 'General Inquiry' && isSponsor) return false;
    }

    // Status filter
    if (selectedStatus !== 'all') {
      if (inq.status !== selectedStatus) return false;
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = (inq.name || '').toLowerCase().includes(q);
      const matchEmail = (inq.email || '').toLowerCase().includes(q);
      const matchMsg = (inq.message || '').toLowerCase().includes(q);
      const matchSub = (inq.subject || '').toLowerCase().includes(q);
      return matchName || matchEmail || matchMsg || matchSub;
    }

    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header — Donation Hero Section Palette with Purple Corner Glow & Spinning BOW Logo Refresh Button */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-purple-950 to-orange-950 p-6 sm:p-8 text-white shadow-2xl">
        {/* Purple Glow in corner */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/25 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-600/20 rounded-full blur-2xl pointer-events-none -ml-20 -mb-20"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-semibold text-orange-200">
              <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
              <span>Contact Page Management</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Direct Inquiries & Messages
            </h1>
            <p className="text-sm text-gray-300 max-w-xl">
              Track who contacted BOW for General Inquiries or Sponsorship & Partnerships. View messages, mark status, and reply directly.
            </p>
          </div>

          {/* Action / Refresh with Circular BOW Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportCSV}
              className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all hover:scale-105"
              title="Export all inquiries as CSV"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>

            {/* Logo as Refresh Button */}
            <button
              onClick={() => fetchInquiries(true)}
              className="group relative flex items-center justify-center p-1 rounded-full hover:ring-4 hover:ring-purple-400/40 transition-all duration-300 transform active:scale-95"
              title="Click logo to refresh inquiries"
              aria-label="Refresh inquiries"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden ring-2 ring-white/30 shadow-lg relative bg-white flex items-center justify-center">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="BOW Logo Refresh"
                    className={`w-full h-full object-cover rounded-full ${isRefreshing ? 'animate-spin' : 'group-hover:scale-110'} transition-transform duration-300`}
                  />
                ) : (
                  <Mail className={`w-6 h-6 text-primary-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                )}
              </div>
              {/* Tooltip badge */}
              <span className="absolute -bottom-6 text-[10px] text-purple-200 opacity-0 group-hover:opacity-100 transition-opacity font-medium whitespace-nowrap">
                Click to Refresh
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-md flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Received</p>
            <p className="text-2xl sm:text-3xl font-black text-gray-900 mt-1">{stats.total}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <Mail className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-amber-200/80 shadow-md flex items-center justify-between relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Needs Action</p>
              {stats.unread > 0 && (
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
              )}
            </div>
            <p className="text-2xl sm:text-3xl font-black text-amber-900 mt-1">{stats.unread}</p>
            <p className="text-[11px] text-amber-600 font-medium mt-0.5">Unread messages</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <AlertCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-md flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">General Inquiries</p>
            <p className="text-2xl sm:text-3xl font-black text-blue-600 mt-1">{stats.generalInquiries}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Community questions</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <MessageSquare className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-md flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sponsorships</p>
            <p className="text-2xl sm:text-3xl font-black text-purple-600 mt-1">{stats.sponsorshipInquiries}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Partnership leads</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
            <Heart className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200/80 shadow-sm space-y-4">
        {/* Topic Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Topic:</span>
            <button
              onClick={() => setSelectedTopic('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                selectedTopic === 'all'
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Topics ({stats.total})
            </button>
            <button
              onClick={() => setSelectedTopic('General Inquiry')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                selectedTopic === 'General Inquiry'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              General Inquiry ({stats.generalInquiries})
            </button>
            <button
              onClick={() => setSelectedTopic('Sponsorship & Partnerships')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                selectedTopic === 'Sponsorship & Partnerships'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
              }`}
            >
              Sponsorship & Partnerships ({stats.sponsorshipInquiries})
            </button>
          </div>

          {/* Status selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Status:</span>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-200 font-medium text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="unread">Unread Only ({stats.unread})</option>
              <option value="read">Read ({stats.read})</option>
              <option value="replied">Replied ({stats.replied})</option>
            </select>
          </div>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, topic, or message contents..."
            className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Inquiry List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm">
          <div className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-gray-500 font-medium">Loading inquiries from database...</p>
        </div>
      ) : filteredInquiries.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8" />
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">No Inquiries Found</h3>
          <p className="text-xs text-gray-500 max-w-sm mx-auto">
            {searchQuery || selectedTopic !== 'all' || selectedStatus !== 'all'
              ? 'No inquiries match your current filters. Try changing or clearing them.'
              : 'Messages sent from the Contact page form will automatically appear here!'}
          </p>
        </div>
      ) : (
        <div className="space-y-3.5">
          {filteredInquiries.map((inq) => {
            const isSponsorship = (inq.subject || '').toLowerCase().includes('sponsor');
            const isUnread = inq.status === 'unread';

            return (
              <div
                key={inq.id}
                className={`bg-white rounded-2xl p-5 border transition-all duration-200 hover:shadow-lg ${
                  isUnread
                    ? 'border-amber-300 bg-amber-50/20 shadow-sm'
                    : 'border-gray-200/80'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left: Sender info & topic */}
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-extrabold text-base text-gray-900">
                        {inq.name}
                      </span>
                      
                      {/* Topic Tag */}
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          isSponsorship
                            ? 'bg-purple-100 text-purple-700 border border-purple-200'
                            : 'bg-blue-100 text-blue-700 border border-blue-200'
                        }`}
                      >
                        {isSponsorship ? <Heart className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                        {inq.subject || 'General Inquiry'}
                      </span>

                      {/* Status Tag */}
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                          inq.status === 'replied'
                            ? 'bg-emerald-100 text-emerald-800'
                            : inq.status === 'read'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-amber-100 text-amber-800 animate-pulse'
                        }`}
                      >
                        {inq.status || 'unread'}
                      </span>
                    </div>

                    {/* Email, Phone & Date */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1 text-gray-700 font-semibold">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        <a
                          href={`mailto:${inq.email}?subject=Re: Beats of Washington - ${encodeURIComponent(inq.subject || 'Inquiry')}`}
                          className="hover:text-primary-600 hover:underline"
                        >
                          {inq.email}
                        </a>
                        <button
                          onClick={() => handleCopyEmail(inq.email, inq.id)}
                          className="p-1 hover:text-primary-600 text-gray-400"
                          title="Copy email address"
                        >
                          {copiedId === inq.id ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>

                      {inq.phone && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1 text-gray-700 font-semibold">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            <a
                              href={`tel:${inq.phone}`}
                              className="hover:text-primary-600 hover:underline"
                            >
                              {inq.phone}
                            </a>
                          </div>
                        </>
                      )}

                      <span>•</span>
                      
                      <div className="flex items-center gap-1 text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(inq.createdAt).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Message Preview */}
                    <p className="text-xs sm:text-sm text-gray-700 mt-2 line-clamp-2 bg-gray-50 rounded-xl p-3 border border-gray-100">
                      {inq.message}
                    </p>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 self-end lg:self-center shrink-0">
                    <button
                      onClick={() => setActiveModalInquiry(inq)}
                      className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold flex items-center gap-1.5 transition-colors"
                      title="View full message details"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>View</span>
                    </button>

                    <a
                      href={`mailto:${inq.email}?subject=Re: Beats of Washington - ${encodeURIComponent(inq.subject || 'Inquiry')}&body=Hi ${encodeURIComponent(inq.name)},\n\nThank you for reaching out to Beats of Washington regarding "${encodeURIComponent(inq.subject)}".\n\n`}
                      onClick={() => handleUpdateStatus(inq.id, 'replied')}
                      className="px-3 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-orange-600 hover:from-primary-700 hover:to-orange-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all hover:scale-105"
                      title="Reply via email client and mark as Replied"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      <span>Reply</span>
                    </a>

                    {inq.status !== 'read' && inq.status !== 'replied' ? (
                      <button
                        onClick={() => handleUpdateStatus(inq.id, 'read')}
                        className="px-2.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold"
                        title="Mark as Read"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpdateStatus(inq.id, 'unread')}
                        className="px-2.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold"
                        title="Mark as Unread"
                      >
                        Unread
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(inq.id)}
                      className="p-2 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete inquiry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Detail Modal */}
      {activeModalInquiry && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setActiveModalInquiry(null)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 relative border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveModalInquiry(null)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 mb-4">
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${
                  (activeModalInquiry.subject || '').toLowerCase().includes('sponsor')
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {activeModalInquiry.subject || 'General Inquiry'}
              </span>
              <span className="text-xs text-gray-400">
                Received: {new Date(activeModalInquiry.createdAt).toLocaleString()}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-1">
              {activeModalInquiry.name}
            </h2>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
              <div className="flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-gray-400" />
                <a href={`mailto:${activeModalInquiry.email}`} className="text-primary-600 font-semibold hover:underline">
                  {activeModalInquiry.email}
                </a>
              </div>
              {activeModalInquiry.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${activeModalInquiry.phone}`} className="text-primary-600 font-semibold hover:underline">
                    {activeModalInquiry.phone}
                  </a>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200/80 mb-6">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Message Body</h4>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {activeModalInquiry.message}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500">Status:</span>
                <select
                  value={activeModalInquiry.status || 'unread'}
                  onChange={(e) => handleUpdateStatus(activeModalInquiry.id, e.target.value)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white font-medium"
                >
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={`mailto:${activeModalInquiry.email}?subject=Re: Beats of Washington - ${encodeURIComponent(activeModalInquiry.subject || 'Inquiry')}`}
                  onClick={() => handleUpdateStatus(activeModalInquiry.id, 'replied')}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-orange-600 text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-primary-500/25 hover:scale-105 transition-all"
                >
                  <Mail className="w-4 h-4" />
                  <span>Reply via Email</span>
                </a>
                <button
                  onClick={() => handleDelete(activeModalInquiry.id)}
                  className="px-3 py-2 rounded-xl text-red-600 hover:bg-red-50 text-xs font-bold transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiriesManagement;
